'use client'

import { forwardRef } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import { imageClasses } from './classes'
import type { ImageProps } from './types'

// --------------------------------------------------------------------------------------------- //

export const Image = forwardRef<HTMLSpanElement, ImageProps>(
  (
    {
      alt,
      src,
      ratio,
      delayTime,
      threshold,
      beforeLoad,
      delayMethod,
      placeholder,
      wrapperProps,
      scrollPosition,
      effect = 'blur',
      visibleByDefault,
      wrapperClassName,
      disabledEffect = false,
      useIntersectionObserver,
      //
      sx,
      slotProps,
      className,
      ...other
    },
    ref
  ) => {
    return (
      <ImageWrapper
        ref={ref}
        id={'image-wrapper'}
        className={imageClasses.root.concat(className ? ` ${className}` : '')}
        sx={{ ...(!!ratio && { width: 1 }), ...(!!other?.width && { width: other?.width }), ...sx }}
        {...other}
      >
        {/** OVERLAY **/}
        {slotProps?.overlay && <Overlay id={'image-overlay'} className={imageClasses.overlay} sx={slotProps?.overlay} />}

        {/** CONTENT **/}
        <Box
          id={'image-box'}
          component={LazyLoadImage}
          alt={alt}
          src={src}
          delayTime={delayTime}
          threshold={threshold}
          beforeLoad={beforeLoad}
          delayMethod={delayMethod}
          placeholder={placeholder}
          wrapperProps={wrapperProps}
          scrollPosition={scrollPosition}
          visibleByDefault={visibleByDefault}
          effect={visibleByDefault || disabledEffect ? undefined : effect}
          useIntersectionObserver={useIntersectionObserver}
          wrapperClassName={wrapperClassName || imageClasses.wrapper}
          placeholderSrc={visibleByDefault || disabledEffect ? `/assets/core/transparent.png` : `/assets/core/placeholder.svg`}
          sx={{
            width: 1,
            height: 1,
            objectFit: 'cover',
            verticalAlign: 'bottom',
            aspectRatio: ratio
          }}
        />
      </ImageWrapper>
    )
  }
)

// --------------------------------------------------------------------------------------------- //

const ImageWrapper = styled('span')({
  overflow: 'hidden',
  position: 'relative',
  verticalAlign: 'bottom',
  display: 'inline-block',
  // Added maxWidth: '100%' so that images don't overflow their containers
  maxWidth: '100%',
  [`& .${imageClasses.wrapper}`]: {
    width: '100%',
    height: '100%',
    verticalAlign: 'bottom',
    backgroundSize: 'cover !important'
  }
})

const Overlay = styled('span')({
  top: 0,
  left: 0,
  zIndex: 1,
  width: '100%',
  height: '100%',
  position: 'absolute'
})
